import {
  Button, Chip, Input, Table, TableBody, TableCell, TableColumn, TableHeader, TableRow, Textarea,
} from '@heroui/react';
import { ArrowLeft, Eye, Upload } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ContentLoadError, PageTitle, PageWrapper, Spinner } from '../../../components';
import {
  useGetStripeTermsVersion,
  useGetStripeTermsVersions,
  usePublishStripeTerms,
} from '../../../queries';
import { formatFirestoreDate } from '../../../utils/formatFirestoreDate';
import {
  HotelLink, Muted, TableCard, TileHint, TileLabel, Toolbar, WhiteCard,
} from './StripeDashboard.styled';

/**
 * Regulamin płatności online — publikacja nowej wersji.
 *
 * DLACZEGO TO TU, A NIE W KODZIE FRONTU: treść pisze dział prawny, a jest ona podstawą
 * naszej prowizji i przerzucenia zwrotów oraz sporów na hotel. Wgranie nowej wersji nie
 * może czekać na wdrożenie panelu.
 *
 * PUBLIKACJA JEST NIEODWRACALNA W OBRĘBIE NUMERU: backend odmawia zapisu pod istniejącym
 * identyfikatorem. Hotel, który kliknął zgodę na „2026-09-01", musi zobaczyć DOKŁADNIE
 * ten tekst także za dwa lata, więc poprawka literówki to nowa wersja, nie podmiana starej.
 *
 * PODBICIE NUMERU ODNAWIA ZGODY WSZYSTKICH HOTELI — panel poprosi każdy z nich o ponowną
 * akceptację, a do tego czasu pokazuje, że regulamin jest nieaktualny. To jest właściwe
 * zachowanie przy zmianie warunków i kosztowne przy literówce, stąd ostrzeżenie na ekranie.
 */

const FORMAT_HINT = 'Bloki oddzielone pustą linią. Pierwsza linia bloku to tytuł sekcji, '
  + 'kolejne linie to jej akapity.';

/* `admin.firestore.Timestamp` wraca z backendu jako `{_seconds, _nanoseconds}`, ale bywa
   też `{seconds}`. Wspólny helper hs-admina zna oba kształty, więc nie powielamy tu
   trzeciego wariantu tej samej konwersji. */
const dateTime = (value: unknown): string => formatFirestoreDate(
  value,
  { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' },
  'pl-PL',
  '—',
);

export const StripeTerms = () => {
  const navigate = useNavigate();
  const overview = useGetStripeTermsVersions();
  const publish = usePublishStripeTerms();

  const [version, setVersion] = useState('');
  const [note, setNote] = useState('');
  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [published, setPublished] = useState<string | null>(null);
  /** Wersja otwarta do podglądu; jej treść wpada do edytora jako punkt wyjścia. */
  const [loadVersion, setLoadVersion] = useState<string | null>(null);

  const loaded = useGetStripeTermsVersion(loadVersion);

  // Wczytanie starej wersji NADPISUJE edytor — to jest cała funkcja tego przycisku
  // („zacznij od poprzedniej"), więc dzieje się dopiero po kliknięciu, nie samo z siebie.
  useEffect(() => {
    if (loaded.data && loaded.data.version === loadVersion) setText(loaded.data.text);
  }, [loaded.data, loadVersion]);

  const handlePublish = (): void => {
    setError(null);
    setPublished(null);
    publish.mutate({ version: version.trim(), text, note: note.trim() || undefined }, {
      onSuccess: (data) => {
        setPublished(data.version);
        setVersion('');
        setNote('');
        overview.refetch();
      },
      onError: (err: any) => setError(
        err?.response?.data?.message || err?.message || 'Nie udało się opublikować wersji.',
      ),
    });
  };

  if (overview.isError) return <PageWrapper><ContentLoadError /></PageWrapper>;

  const current = overview.data?.currentVersion;
  const builtIn = overview.data?.builtInVersion;

  return (
    <PageWrapper>
      <PageTitle title="Regulamin płatności online" />

      <Toolbar>
        <Button
          size="sm"
          variant="light"
          startContent={<ArrowLeft size={14} />}
          onPress={() => navigate('/stripe')}
        >
          Wróć do prowizji
        </Button>
      </Toolbar>

      {overview.isPending ? <Spinner /> : (
        <>
          <WhiteCard style={{ marginBottom: 24 }}>
            <TileLabel>Wersja obowiązująca</TileLabel>
            <p style={{ fontSize: 18, fontWeight: 600, margin: '4px 0' }}>
              {current || <Muted>{`${builtIn} (wbudowana w panel)`}</Muted>}
            </p>
            <TileHint>
              {current
                ? 'Tę wersję akceptują hotele włączające płatności online.'
                : 'Nic jeszcze nie opublikowano, więc panel pokazuje dokument wbudowany w kod. '
                  + 'Pierwsza publikacja przejmuje nad nim pierwszeństwo.'}
            </TileHint>
          </WhiteCard>

          <WhiteCard style={{ marginBottom: 24 }}>
            <TileLabel>Nowa wersja</TileLabel>
            <TileHint style={{ marginBottom: 12 }}>
              Publikacja poprosi WSZYSTKIE hotele o ponowną akceptację, a do tego czasu ich
              płatności online działają na wersji poprzedniej. Numeru nie da się użyć drugi raz.
            </TileHint>

            <div style={{ display: 'flex', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
              <Input
                size="sm"
                label="Numer wersji"
                placeholder="2026-09-01-v2"
                value={version}
                onValueChange={setVersion}
                className="max-w-[220px]"
                description="Litery, cyfry, kropka, myślnik. Bez spacji."
              />
              <Input
                size="sm"
                label="Notatka (tylko dla nas)"
                placeholder="Zmiana zasad zwrotów"
                value={note}
                onValueChange={setNote}
                className="max-w-[320px]"
              />
            </div>

            <Textarea
              label="Treść regulaminu"
              placeholder={FORMAT_HINT}
              description={FORMAT_HINT}
              value={text}
              onValueChange={setText}
              minRows={14}
              maxRows={28}
            />

            {error && (
              <p style={{ color: '#b91c1c', fontSize: 13, marginTop: 10 }}>{error}</p>
            )}
            {published && (
              <p style={{ color: '#047857', fontSize: 13, marginTop: 10 }}>
                {`Opublikowano wersję ${published}. Hotele zostaną poproszone o ponowną akceptację.`}
              </p>
            )}

            <div style={{ marginTop: 14 }}>
              <Button
                size="sm"
                color="primary"
                startContent={<Upload size={14} />}
                isLoading={publish.isPending}
                isDisabled={!version.trim() || !text.trim()}
                onPress={handlePublish}
              >
                Opublikuj wersję
              </Button>
            </div>
          </WhiteCard>

          <TableCard>
            <Table aria-label="Opublikowane wersje regulaminu" removeWrapper>
              <TableHeader>
                <TableColumn>WERSJA</TableColumn>
                <TableColumn>OPUBLIKOWANO</TableColumn>
                <TableColumn>PRZEZ</TableColumn>
                <TableColumn>NOTATKA</TableColumn>
                <TableColumn>SEKCJI</TableColumn>
                <TableColumn> </TableColumn>
              </TableHeader>
              <TableBody emptyContent="Brak opublikowanych wersji.">
                {(overview.data?.versions ?? []).map((row) => (
                  <TableRow key={row.version}>
                    <TableCell>
                      {row.version}
                      {row.version === current && (
                        <Chip size="sm" color="success" variant="flat" style={{ marginLeft: 8 }}>
                          obowiązuje
                        </Chip>
                      )}
                    </TableCell>
                    <TableCell>{dateTime(row.publishedAt)}</TableCell>
                    <TableCell>{row.publishedBy || <Muted>—</Muted>}</TableCell>
                    <TableCell>{row.note || <Muted>—</Muted>}</TableCell>
                    <TableCell>{row.sectionsCount}</TableCell>
                    <TableCell>
                      {/* Wczytanie do edytora, nie podmiana: nową wersję prawie zawsze
                          robi się z poprzedniej, zmieniając jeden paragraf. */}
                      <HotelLink type="button" onClick={() => setLoadVersion(row.version)}>
                        <Eye size={13} style={{ display: 'inline', marginRight: 4 }} />
                        Wczytaj do edytora
                      </HotelLink>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableCard>
        </>
      )}
    </PageWrapper>
  );
};
